import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { Incident, IncidentList, IncidentStatus, Priority } from '../lib/types';
import { INCIDENT_STATUS_LABELS } from '../lib/types';
import { useSession } from '../lib/session';
import { useRememberedCompany } from '../lib/rememberedCompany';
import { PriorityBadge } from '../components/Badges';
import { formatDateTime } from '../lib/dates';

/**
 * Incidents: what is broken, for whom, and whether it has happened before.
 *
 * Deliberately not the delivery board. An incident carries no points and no sprint — its job
 * is to be counted, because four reports of one fault are the evidence that there is a
 * problem worth a root cause. The column that earns this page its keep is "Cause": once a
 * problem explains an incident, the workaround is right there for whoever picks up the phone.
 */
export function IncidentsPage() {
  const { session } = useSession();
  const params = useParams();
  const navigate = useNavigate();

  const companies = session?.companies ?? [];
  const { companyId: remembered, remember } = useRememberedCompany(companies);
  const companyId = params.companyId ?? remembered;

  const [data, setData] = useState<IncidentList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('open_only');
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setData(await api.get<IncidentList>(`/companies/${companyId}/incidents?status=${status}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the incidents');
    }
  }, [companyId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!params.companyId && companyId) navigate(`/companies/${companyId}/incidents`, { replace: true });
  }, [params.companyId, companyId, navigate]);

  async function act(what: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await what();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  if (companies.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <h3>No company access</h3>
          <p>Incidents are per company, and you have not been given access to one yet.</p>
        </div>
      </div>
    );
  }

  const counts = data?.counts ?? {};
  const liveCount = (counts.open ?? 0) + (counts.investigating ?? 0) + (counts.waiting ?? 0);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Service</div>
          <h1>Incidents</h1>
          <p className="subtitle">
            What is broken and for whom. An incident is a report, not a piece of delivery work —
            its job is to be counted, because several reports of one fault are how a problem
            gets found.
          </p>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ marginBottom: 0, minWidth: 150 }}>
            <span className="field-label">Showing</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="open_only">Still open ({liveCount})</option>
              <option value="all">Everything</option>
              <option value="resolved">Resolved ({counts.resolved ?? 0})</option>
              <option value="closed">Closed ({counts.closed ?? 0})</option>
            </select>
          </label>
          <label className="field" style={{ marginBottom: 0, minWidth: 190 }}>
            <span className="field-label">Company</span>
            <select
              value={companyId}
              onChange={(event) => {
                remember(event.target.value);
                navigate(`/companies/${event.target.value}/incidents`);
              }}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && <div className="callout mb-2">{error}</div>}

      <ReportForm companyId={companyId} projects={data?.projects ?? []} onDone={load} />

      {!data ? (
        <div className="empty">Loading…</div>
      ) : data.incidents.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>Nothing broken</h3>
            <p>
              No incidents match that filter. Freshdesk stays the service desk — this is the
              record that lets a ticket be counted against a cause.
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>What is wrong</th>
                  <th>State</th>
                  <th>Priority</th>
                  <th>Project</th>
                  <th>Cause</th>
                  <th>Reported</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.incidents.map((incident) => (
                  <IncidentRow
                    key={incident.id}
                    incident={incident}
                    expanded={open === incident.id}
                    busy={busy}
                    onToggle={() => setOpen(open === incident.id ? null : incident.id)}
                    onAct={act}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function IncidentRow({
  incident,
  expanded,
  busy,
  onToggle,
  onAct,
}: {
  incident: Incident;
  expanded: boolean;
  busy: boolean;
  onToggle: () => void;
  onAct: (what: () => Promise<unknown>) => Promise<void>;
}) {
  const [resolution, setResolution] = useState('');

  return (
    <>
      <tr>
        <td className="mono" style={{ fontSize: 11 }}>
          <button type="button" className="link-button" onClick={onToggle}>
            {incident.reference}
          </button>
          {incident.freshdeskTicketId && (
            <div className="dim" style={{ fontSize: 10 }} title="Shadowed from the service desk">
              FD {incident.freshdeskTicketId}
            </div>
          )}
        </td>
        <td>
          <span style={{ fontWeight: 600 }}>{incident.title}</span>
          {incident.assignee && (
            <div className="dim" style={{ fontSize: 11 }}>
              with {incident.assignee.name}
            </div>
          )}
        </td>
        <td>
          <span className={`sprint-state is-${STATE_TONE[incident.status]}`}>
            {INCIDENT_STATUS_LABELS[incident.status]}
          </span>
        </td>
        <td>
          <PriorityBadge priority={incident.priority as Priority} />
        </td>
        <td style={{ fontSize: 11 }}>
          {incident.project ? <span className="key">{incident.project.code}</span> : <span className="dim">unknown</span>}
        </td>
        <td style={{ fontSize: 11, maxWidth: 220 }}>
          {incident.problem ? (
            <>
              <span className="badge badge-accent" title={incident.problem.title}>
                {incident.problem.reference}
              </span>
              {/* The reason this page exists: support answers the next caller from here. */}
              {incident.problem.workaround && (
                <div className="dim" style={{ marginTop: 3 }}>
                  Workaround: {incident.problem.workaround}
                </div>
              )}
            </>
          ) : (
            <span className="dim">not known yet</span>
          )}
        </td>
        <td className="mono" style={{ fontSize: 11 }}>
          {formatDateTime(incident.reportedAt)}
        </td>
        <td className="right">
          {!incident.editable && (
            <span className="dim" style={{ fontSize: 11 }} title="You can only change an incident you reported">
              read only
            </span>
          )}
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--surface-sunken)' }}>
            <div className="stack-sm" style={{ padding: '10px 4px' }}>
              {incident.problem && (
                <p className="dim" style={{ margin: 0, fontSize: 12 }}>
                  Explained by <strong>{incident.problem.reference}</strong> — {incident.problem.title}
                </p>
              )}

              {incident.editable && incident.status !== 'closed' && incident.status !== 'resolved' && (
                <div className="row wrap" style={{ gap: 7 }}>
                  <input
                    value={resolution}
                    placeholder="What was done about it"
                    onChange={(event) => setResolution(event.target.value)}
                    style={{ minWidth: 280 }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={busy || resolution.trim().length < 3}
                    onClick={() =>
                      void onAct(async () => {
                        await api.post(`/incidents/${incident.id}/resolve`, { resolution: resolution.trim() });
                        setResolution('');
                      })
                    }
                  >
                    Resolve
                  </button>
                  {incident.status === 'open' && (
                    <button
                      type="button"
                      className="btn btn-quiet btn-sm"
                      disabled={busy}
                      onClick={() => void onAct(() => api.patch(`/incidents/${incident.id}`, { status: 'investigating' }))}
                    >
                      Start investigating
                    </button>
                  )}
                </div>
              )}

              {incident.editable && incident.status === 'resolved' && (
                <div className="row" style={{ gap: 7 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={busy}
                    onClick={() => void onAct(() => api.post(`/incidents/${incident.id}/close`, {}))}
                  >
                    Close it
                  </button>
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    disabled={busy}
                    onClick={() => void onAct(() => api.post(`/incidents/${incident.id}/reopen`, {}))}
                  >
                    It came back
                  </button>
                </div>
              )}

              {incident.editable && incident.status === 'closed' && (
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  disabled={busy}
                  onClick={() => void onAct(() => api.post(`/incidents/${incident.id}/reopen`, {}))}
                >
                  Reopen — it came back
                </button>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Reporting one. Short on purpose: the diagnosis comes later, the report should not wait for it. */
function ReportForm({
  companyId,
  projects,
  onDone,
}: {
  companyId: string;
  projects: Array<{ id: string; name: string; code: string }>;
  onDone: () => Promise<void>;
}) {
  const [showing, setShowing] = useState(false);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [projectId, setProjectId] = useState('');
  const [ticket, setTicket] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!showing) {
    return (
      <div className="row mb-2">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowing(true)}>
          Report an incident
        </button>
      </div>
    );
  }

  return (
    <div className="card mb-2">
      <div className="card-head">
        <h3>Report an incident</h3>
      </div>
      <div className="card-pad stack-sm">
        {error && <div className="callout">{error}</div>}

        <label className="field" style={{ marginBottom: 0 }}>
          <span className="field-label">What is wrong?</span>
          <input
            value={title}
            autoFocus
            placeholder="Statements will not download"
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <div className="row wrap" style={{ gap: 8 }}>
          <label className="field" style={{ marginBottom: 0, minWidth: 130 }}>
            <span className="field-label">How bad?</span>
            <select value={priority} onChange={(event) => setPriority(event.target.value as Priority)}>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>

          <label className="field" style={{ marginBottom: 0, minWidth: 170 }}>
            <span className="field-label">Project, if known</span>
            <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              <option value="">Not known yet</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field" style={{ marginBottom: 0, minWidth: 150 }}>
            <span className="field-label">Freshdesk ticket</span>
            <input value={ticket} placeholder="optional" onChange={(event) => setTicket(event.target.value)} />
          </label>
        </div>

        <div className="row" style={{ gap: 7 }}>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy || title.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.post(`/companies/${companyId}/incidents`, {
                  title: title.trim(),
                  priority,
                  ...(projectId ? { projectId } : {}),
                  ...(ticket.trim() ? { freshdeskTicketId: ticket.trim() } : {}),
                });
                setTitle('');
                setTicket('');
                setProjectId('');
                setShowing(false);
                await onDone();
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'Could not report it');
              } finally {
                setBusy(false);
              }
            }}
          >
            Report it
          </button>
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => setShowing(false)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Reuses the sprint-state pill, which already reads as a state rather than a label. */
const STATE_TONE: Record<IncidentStatus, string> = {
  open: 'active',
  investigating: 'active',
  waiting: 'planned',
  resolved: 'completed',
  closed: 'completed',
};
